import { getMeasureFromTitle, getMemberFromTitle } from './explore'
import { DIMENSION_TITLES, MEASURE_TITLES } from '~/constants/analytics'
import { ANALYTICS_GET_QUERY } from '~/gql/analytics/analyticsGetQuery'
import { CHECK_DATA_HEALTH_STATUS_QUERY } from '~/gql/dataQuality/checkDataHealthStatus'
import { useCubeStore } from '~/stores/cube'
import { DataType, type Filter } from '~/types/analytics'
import {
  DataHealthCheckStatus,
  NormalizationCheckStatus,
  type DataHealthCheck,
  type DataHealthCheckDetailDto
} from '~/types/dataQuality'
import { ScopeType } from '~/types/permissions'
import type {
  NormalizedFieldEntry,
  SourceValueDimension
} from '~/types/sourceValue'
import { filterNullValue } from '~/types/utils'
import type { DateRange } from '~/types/timeDimension'
import { CHECK_NORMALIZATION_STATUS_QUERY } from '~/gql/dataQuality/checkNormalizationStatus'
import { GET_DATA_HEALTH_CHECK_DETAIL_QUERY } from '~/gql/dataQuality/getDataHealthCheckDetailQuery'

export const getPersonCount = async (
  dataType: DataType,
  dateRange: string[],
  filters: Filter[]
) => {
  const { $apiGqlClient } = useNuxtApp()

  const query = {
    dimensions: [],
    measures: [
      dataType === DataType.EMPLOYEE
        ? MEASURE_TITLES.EMPLOYEE_COUNT_OVER_PERIOD
        : MEASURE_TITLES.RECRUITMENT_COUNT_HIRE
    ],
    filters,
    timeDimensions: [
      {
        dimension: DIMENSION_TITLES.DATE,
        dateRange
      }
    ]
  }

  return (
    await $apiGqlClient.query({
      query: ANALYTICS_GET_QUERY,
      variables: {
        query,
        scopeType: ScopeType.COMPANY
      }
    })
  ).data.analyticsGetQuery.data[0][getMeasureFromTitle(query.measures[0]!).name]
}

export const getNormalizationChecksFromMembers = (
  members: string[]
): NormalizedFieldEntry[] => {
  return [
    ...new Set(
      members
        .map(memberTitle => getMemberFromTitle(memberTitle))
        .flatMap(member => member?.meta.normalizationFieldEntries ?? [])
    )
  ]
}

export const getSourceValueDimensionsFromMembers = (
  members: string[]
): SourceValueDimension[] => {
  return filterNullValue([
    ...new Set(
      members
        .map(memberTitle => getMemberFromTitle(memberTitle))
        .map(member =>
          member?.meta && 'sourceValueDimension' in member?.meta
            ? member.meta.sourceValueDimension
            : null
        )
    )
  ])
}

export const getDataHealthChecksFromMembers = (
  members: string[]
): DataHealthCheck[] => {
  return [
    ...new Set(
      members
        .map(memberTitle => getMemberFromTitle(memberTitle))
        .flatMap(member => member?.meta.dataHealthChecks ?? [])
    )
  ]
}

export const getImpactedMeasuresFromHealthCheck = (
  dataHealthCheck: DataHealthCheck
) => {
  const { measuresMeta } = useCubeStore()

  return measuresMeta?.filter(
    measure =>
      measure.meta.dataHealthChecks?.includes(dataHealthCheck) &&
      measure.meta.showInExplorer
  )
}

export const getDataHealthCheckStatus = async (
  dataHealthCheck: DataHealthCheck,
  dateRange: DateRange,
  filters: Filter[]
): Promise<DataHealthCheckStatus> => {
  const app = useNuxtApp()
  return (
    await app.$apiGqlClient.query({
      query: CHECK_DATA_HEALTH_STATUS_QUERY,
      fetchPolicy: 'cache-first',
      variables: {
        check: {
          dataHealthCheck,
          startDate: dateRange[0],
          endDate: dateRange[1],
          filters
        }
      }
    })
  ).data.getDataHealthCheckStatus.status as DataHealthCheckStatus
}

export const getDataHealthCheckDetail = async (
  dataHealthCheck: DataHealthCheck,
  dateRange: DateRange,
  filters: Filter[]
): Promise<DataHealthCheckDetailDto> => {
  const app = useNuxtApp()

  return (
    await app.$apiGqlClient.query({
      query: GET_DATA_HEALTH_CHECK_DETAIL_QUERY,
      fetchPolicy: 'cache-first',
      variables: {
        check: {
          dataHealthCheck,
          filters,
          startDate: dateRange[0],
          endDate: dateRange[1]
        }
      }
    })
  ).data.getDataHealthCheckDetail as DataHealthCheckDetailDto
}

export const getNormalizationStatus = async (
  normalizationCheck: NormalizedFieldEntry,
  dateRange: DateRange
) => {
  const app = useNuxtApp()

  return (
    await app.$apiGqlClient.query({
      query: CHECK_NORMALIZATION_STATUS_QUERY,
      variables: {
        filters: {
          normalizationFieldEntry: normalizationCheck,
          startDate: dateRange[0],
          endDate: dateRange[1]
        }
      }
    })
  ).data.getNormalizationStatus as NormalizationCheckStatus
}
